import React, { useState, useEffect, useCallback } from 'react';
import { PageShell, StatCard } from '../../components/Ui';
import { MdRestaurant, MdPerson, MdCheckCircle } from 'react-icons/md';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';

const DIET_TYPES = ['Normal', 'Diabetic', 'Low Sodium', 'Low Fat', 'High Protein', 'Liquid', 'Soft Diet', 'Renal Diet', 'Post-Op', 'NPO (Nothing by Mouth)'];
const MEAL_TYPES = ['Breakfast', 'Mid-Morning', 'Lunch', 'Afternoon Snack', 'Dinner', 'Bedtime'];

const DEFAULT_MENUS = {
  Normal: {
    Breakfast: 'Idli/Dosa + Sambar + Chutney + Tea/Coffee',
    'Mid-Morning': 'Fruits / Biscuits + Juice',
    Lunch: 'Rice + Dal + Sabzi + Curd + Chapati',
    'Afternoon Snack': 'Tea + Snack (Poha/Upma)',
    Dinner: 'Chapati + Dal + Sabzi + Rice',
    Bedtime: 'Milk (warm)',
  },
  Diabetic: {
    Breakfast: 'Oats/Idli (less rice) + Sambar + Tea (no sugar)',
    'Mid-Morning': 'Sprouts / Cucumber + Buttermilk',
    Lunch: 'Brown rice (small) + Dal + Green Sabzi + Salad',
    'Afternoon Snack': 'Roasted Chana / Nuts',
    Dinner: 'Multi-grain Chapati + Dal + Sabzi',
    Bedtime: 'Low-fat Milk (no sugar)',
  },
  Liquid: {
    Breakfast: 'Fruit Juice / Coconut Water',
    'Mid-Morning': 'Buttermilk / Lassi',
    Lunch: 'Clear Soup / Lentil Soup',
    'Afternoon Snack': 'Tender Coconut Water',
    Dinner: 'Daliya Soup / Vegetable Broth',
    Bedtime: 'Warm Milk',
  },
};

function orderMap(orders) {
  const m = {};
  (orders || []).forEach((o) => { m[o.admissionId] = o; });
  return m;
}

export default function DietManagementPage() {
  const { data, loading } = useLiveData(() => ipApi.getAdmissions(50), []);
  const { data: dietData, reload: reloadDiet } = useLiveData(() => ipApi.getDietOrders(100), []);
  const admissions = data?.admissions || [];
  const savedByAdm = orderMap(dietData?.orders);

  const [localOrders, setLocalOrders] = useState({});
  const [tab, setTab] = useState('orders');
  const [selectedDiet, setSelectedDiet] = useState('Normal');
  const [saving, setSaving] = useState('');
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    const next = {};
    admissions.forEach((adm) => {
      const saved = savedByAdm[adm.admissionId];
      next[adm.admissionId] = saved || {
        admissionId: adm.admissionId,
        patientName: adm.patientName,
        ward: adm.ward,
        bed: adm.bedLabel || adm.bedId,
        dietType: 'Normal',
        allergyNote: '',
        spInstruction: '',
        mealDelivered: {},
      };
    });
    setLocalOrders((prev) => ({ ...prev, ...next }));
  }, [admissions, dietData]);

  const getOrder = (admId) => localOrders[admId] || {};

  const persistOrder = useCallback(async (admId, patch) => {
    const adm = admissions.find((a) => a.admissionId === admId);
    const merged = { ...getOrder(admId), ...patch, admissionId: admId };
    setLocalOrders((d) => ({ ...d, [admId]: merged }));
    setSaving(admId);
    setSaveMsg('');
    try {
      await ipApi.saveDietOrder(admId, {
        patientName: merged.patientName || adm?.patientName,
        ward: merged.ward || adm?.ward,
        bed: merged.bed || adm?.bedLabel || adm?.bedId,
        dietType: merged.dietType,
        allergyNote: merged.allergyNote,
        spInstruction: merged.spInstruction,
        mealDelivered: merged.mealDelivered,
      });
      await reloadDiet();
    } catch (e) {
      setSaveMsg(e.message);
    } finally {
      setSaving('');
    }
  }, [admissions, localOrders, reloadDiet]);

  const setDietOrder = (admId, patch) => {
    persistOrder(admId, patch);
  };

  const markMeal = (admId, meal) => {
    const order = getOrder(admId);
    const mealDelivered = { ...(order.mealDelivered || {}), [meal]: !order.mealDelivered?.[meal] };
    persistOrder(admId, { mealDelivered });
  };

  const dietOrders = admissions.map((a) => getOrder(a.admissionId)).filter((o) => o.admissionId);
  const menuForDiet = DEFAULT_MENUS[selectedDiet] || DEFAULT_MENUS.Normal;

  return (
    <PageShell
      title="Diet & Nutrition Management"
      subtitle="Inpatient diet orders · Meal planning · Delivery tracking (saved to database)"
    >
      {saveMsg && <div className="error mb">{saveMsg}</div>}
      <div className="stat-grid">
        <StatCard label="Patients with Diet Orders" value={dietOrders.length} accent="#10b981" icon={MdRestaurant} loading={loading} />
        <StatCard label="Special Diets" value={dietOrders.filter((d) => d.dietType !== 'Normal').length} accent="#f97316" icon={MdPerson} />
        <StatCard label="NPO Patients" value={dietOrders.filter((d) => d.dietType === 'NPO (Nothing by Mouth)').length} accent="#ef4444" icon={MdCheckCircle} />
        <StatCard label="Total Inpatients" value={admissions.length} accent="#0d6e6a" loading={loading} />
      </div>

      <div className="period-tabs card mb">
        {[
          { id: 'orders', label: 'Diet Orders' },
          { id: 'menu', label: 'Standard Menus' },
          { id: 'delivery', label: 'Meal Delivery' },
        ].map(({ id, label }) => (
          <button key={id} type="button" className={`emr-chip ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>

      {tab === 'orders' && (
        <div className="card">
          <h3>Diet Orders — All Inpatients</h3>
          <p className="hint mb">Changes save automatically to DynamoDB.</p>
          {admissions.length === 0 && !loading && <p className="hint table-empty">No inpatients found.</p>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {admissions.map((adm) => {
              const order = getOrder(adm.admissionId);
              return (
                <div key={adm.admissionId} className="diet-order-card">
                  <div className="diet-card-head">
                    <div className="diet-patient-info">
                      <div className="diet-patient-name">{adm.patientName}</div>
                      <div className="diet-patient-meta">{adm.ward} · Bed {adm.bedLabel || adm.bedId} · Dr. {adm.doctor}</div>
                    </div>
                    <select
                      className="input"
                      style={{ width: 200, fontWeight: 600 }}
                      value={order.dietType || 'Normal'}
                      disabled={saving === adm.admissionId}
                      onChange={(e) => setDietOrder(adm.admissionId, { dietType: e.target.value })}
                    >
                      {DIET_TYPES.map((d) => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="diet-card-fields">
                    <div className="field">
                      <label>Allergy / Intolerance</label>
                      <input className="input" placeholder="e.g. No eggs, No dairy" value={order.allergyNote || ''} onBlur={(e) => setDietOrder(adm.admissionId, { allergyNote: e.target.value })} onChange={(e) => setLocalOrders((d) => ({ ...d, [adm.admissionId]: { ...getOrder(adm.admissionId), allergyNote: e.target.value } }))} />
                    </div>
                    <div className="field">
                      <label>Special Instructions</label>
                      <input className="input" placeholder="e.g. Low spice, extra soft" value={order.spInstruction || ''} onBlur={(e) => setDietOrder(adm.admissionId, { spInstruction: e.target.value })} onChange={(e) => setLocalOrders((d) => ({ ...d, [adm.admissionId]: { ...getOrder(adm.admissionId), spInstruction: e.target.value } }))} />
                    </div>
                  </div>
                  {(order.dietType === 'NPO (Nothing by Mouth)') && (
                    <div className="diet-npo-banner">NPO — DO NOT SERVE FOOD OR LIQUIDS</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'menu' && (
        <div className="card">
          <div className="card-head-row">
            <h3>Standard Diet Menus</h3>
            <select className="input" style={{ width: 200 }} value={selectedDiet} onChange={(e) => setSelectedDiet(e.target.value)}>
              {DIET_TYPES.filter((d) => DEFAULT_MENUS[d]).map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="diet-menu-grid">
            {MEAL_TYPES.map((meal) => (
              <div key={meal} className="diet-menu-card">
                <div className="diet-meal-time">{meal}</div>
                <div className="diet-meal-items">{menuForDiet[meal] || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'delivery' && (
        <div className="card">
          <h3>Meal Delivery Tracking — Today</h3>
          <p className="hint mb">Mark each meal as delivered — saved to database.</p>
          <div className="diet-delivery-table-wrap">
            <table className="diet-delivery-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Ward / Bed</th>
                  <th>Diet Type</th>
                  {MEAL_TYPES.map((m) => <th key={m}>{m.split(' ')[0]}</th>)}
                </tr>
              </thead>
              <tbody>
                {dietOrders.map((order) => (
                  <tr key={order.admissionId}>
                    <td style={{ fontWeight: 600 }}>{order.patientName}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{order.ward} · {order.bed}</td>
                    <td>
                      <span style={{ fontSize: 11, fontWeight: 700, background: order.dietType === 'NPO (Nothing by Mouth)' ? '#fef2f2' : '#f0fdf4', color: order.dietType === 'NPO (Nothing by Mouth)' ? '#dc2626' : '#16a34a', padding: '2px 8px', borderRadius: 999 }}>
                        {order.dietType}
                      </span>
                    </td>
                    {MEAL_TYPES.map((m) => (
                      <td key={m}>
                        {order.dietType === 'NPO (Nothing by Mouth)' ? (
                          <span style={{ color: '#ef4444', fontWeight: 800, fontSize: 14 }}>X</span>
                        ) : (
                          <button
                            type="button"
                            className="mar-cell-btn"
                            style={{ background: order.mealDelivered?.[m] ? '#16a34a' : '#f1f5f9', color: order.mealDelivered?.[m] ? '#fff' : '#94a3b8' }}
                            onClick={() => markMeal(order.admissionId, m)}
                          >
                            {order.mealDelivered?.[m] ? '✓' : '·'}
                          </button>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {dietOrders.length === 0 && <tr><td colSpan={MEAL_TYPES.length + 3} className="hint table-empty">No inpatients.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </PageShell>
  );
}
